import * as Sentry from '@sentry/react'

/**
 * Initialise Sentry for error tracking and user insights.
 *
 * Driven entirely by env vars so we never ship a hard-coded DSN and can
 * disable Sentry in local/dev simply by leaving VITE_SENTRY_DSN unset:
 *
 *   VITE_SENTRY_DSN          - project DSN (required; Sentry is a no-op without it)
 *   VITE_SENTRY_ENVIRONMENT  - environment tag, e.g. "production" / "uat" (optional)
 *   VITE_SENTRY_TRACES_SAMPLE_RATE        - 0..1, performance tracing  (default 0.1)
 *   VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE - 0..1, session replay      (default 0.1)
 *   VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE   - 0..1, replay on error     (default 1.0)
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    // No DSN configured (typical for local dev) - skip init so the app runs
    // normally and we don't emit noise to a non-existent project.
    return
  }

  const sampleRate = (value: string | undefined, fallback: number) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,

    integrations: [
      // Performance / user insights: page loads, navigations, fetch/XHR.
      Sentry.browserTracingIntegration({
        // Basemap tile fetches are noise: they trip the N+1 API Call detector
        // (fisci-front-end #401) and add nothing to traces. Skipping the span
        // also stops trace headers being sent to a third party.
        shouldCreateSpanForRequest: (url) =>
          !/^https:\/\/(api|events)\.mapbox\.com\//.test(url),
      }),
      // Session Replay: records sessions so you can watch what the user did
      // leading up to an error. Inputs and text are masked by default.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance tracing sample rate.
    tracesSampleRate: sampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 0.1),

    // Session Replay sample rates: a small share of all sessions, plus every
    // session that hits an error.
    replaysSessionSampleRate: sampleRate(
      import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE,
      0.1,
    ),
    replaysOnErrorSampleRate: sampleRate(
      import.meta.env.VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE,
      1.0,
    ),

    // Don't send PII (IP addresses, etc.) automatically. We attach a minimal,
    // explicit user identity from AuthContext instead.
    sendDefaultPii: false,
  })
}
